import Papa from 'papaparse';
import { stringify } from 'yaml';
import i18n from '@/shared/i18n';
import { formatNumber } from '@/shared/utils/number-utils';
import { getReadableSourceName, toDisplayString } from '@/shared/utils';
import {
  getFileExtension,
  isFeather2FormatFromBase64,
} from '@/modules/file-renderer/utils';
import { arrowBufferToCsv } from '@/modules/outputs/utils/arrow-to-csv';
import { base64ToArrayBuffer } from '@/modules/outputs/utils/base64';
import {
  extractPrimaryComplexValue,
  getIDPFieldsFromOutput,
  getIDPItemFields,
  isBrowserInstance,
  isDictionary,
  isFile,
  isHtmlString,
  isIDPBundle,
  isIDPBundleList,
  isIDPList,
  isIDPOutput,
  isMarkdownString,
  isReadContentOutput,
  isRecordList,
  isRemoteValue,
  isTable,
} from '@/modules/outputs/utils';
import {
  EOutputCardVariant,
  type TResolvedFile,
  type TResolvedOutputs,
  type TResolvedValue,
} from '@/modules/outputs/types';

export { getReadableFileName } from '@/shared/utils';

/**
 * Fraction digits a card keeps. A card is a summary, so it rounds; the modal's
 * `PrimitiveRenderer` is the surface that reports every digit.
 */
export const OUTPUT_CARD_MAX_FRACTION_DIGITS = 3;

/** The trimmed table model a card or preview renders, however it was sourced. */
export interface IOutputTablePreview {
  headers: string[];
  rows: string[][];
  totalRows: number;
}

/** Placeholder for a cell or field the output carries no value for. */
const EM_DASH = '—';

/** Returns true only for the file-only list variant described by the card spec. */
export function isOutputFileList(
  value: TResolvedValue,
): value is TResolvedFile[] {
  return Array.isArray(value) && value.length > 0 && value.every(isFile);
}

/** Maps a resolved Bishop value to one of the ten visual output variants. */
export function getOutputCardVariant(
  value: TResolvedValue,
): EOutputCardVariant {
  if (value === null) return EOutputCardVariant.EMPTY;
  if (typeof value === 'number') return EOutputCardVariant.NUMBER;
  if (
    isHtmlString(value) ||
    isMarkdownString(value) ||
    isReadContentOutput(value)
  ) {
    return EOutputCardVariant.CODE;
  }
  if (typeof value === 'string') return EOutputCardVariant.TEXT;
  if (
    isIDPOutput(value) ||
    isIDPList(value) ||
    isIDPBundle(value) ||
    isIDPBundleList(value)
  ) {
    return EOutputCardVariant.IDP;
  }
  if (isOutputFileList(value)) return EOutputCardVariant.LIST;
  if (Array.isArray(value) || isTable(value)) {
    return EOutputCardVariant.TABLE;
  }
  if (isFile(value)) return EOutputCardVariant.FILE;
  if (isBrowserInstance(value)) return EOutputCardVariant.BROWSER;
  return EOutputCardVariant.DICT;
}

/**
 * The file extension of a name, but only when the last dot really starts one.
 *
 * Bishop guarantees nothing about a file name, and the last dot need not start
 * an extension: "Q1 2026 v1.2 report" would otherwise yield "2 report" and
 * "2026.01" would yield "01". A real extension is short, alphanumeric and
 * carries at least one letter; anything else yields the empty string so the
 * caller can fall back to a generic label.
 *
 * @param name - File name to read an extension from
 * @returns The lower-case extension, or `''` when the candidate isn't one
 */
export function getValidFileExtension(name: string): string {
  const extension = getFileExtension(name);
  return /^(?=.*[a-z])[a-z0-9]{1,8}$/.test(extension) ? extension : '';
}

/**
 * True when every item is a file, or a record wrapping a single file, which is
 * how the preview layover lists them.
 */
function isListOfFiles(value: TResolvedValue): boolean {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      item =>
        isFile(item) ||
        (isDictionary(item) && isFile(extractPrimaryComplexValue(item))),
    )
  );
}

/**
 * The shape an array is shown as: a table for tables and lists of records,
 * files for a list of files, and a plain list otherwise.
 */
function getArrayTypeLabel(value: TResolvedValue): string {
  if (isTable(value) || isRecordList(value)) return 'table';
  if (isListOfFiles(value)) return 'files';
  return 'list';
}

/** Type tag shown in the lg card header and the preview modal. */
export function getOutputCardTypeLabel(
  value: TResolvedValue,
  variant: EOutputCardVariant,
): string {
  if (isRemoteValue(value))
    return value.kind === 'dictionary' ? 'yaml' : value.kind;
  // A switch, not an object literal: the literal evaluated EVERY entry on
  // every call, so the FILE arm read a file name off a null output and threw.
  switch (variant) {
    case EOutputCardVariant.TEXT:
      return 'text';
    case EOutputCardVariant.NUMBER:
      return 'number';
    case EOutputCardVariant.LIST:
      return 'files';
    case EOutputCardVariant.TABLE:
      return getArrayTypeLabel(value);
    case EOutputCardVariant.IDP:
      return 'IDP';
    case EOutputCardVariant.FILE:
      return (
        getValidFileExtension(getFileName(value as TResolvedFile)) || 'file'
      );
    case EOutputCardVariant.CODE:
      return isHtmlString(value) ? 'html' : 'markdown';
    case EOutputCardVariant.BROWSER:
      return 'replay';
    case EOutputCardVariant.DICT:
      return 'yaml';
    case EOutputCardVariant.EMPTY:
      return 'null';
  }
}

/**
 * Flattens a `read content` output — Bishop returns one entry per source
 * document — into the single block of text a card or preview shows, blank
 * paragraph between documents.
 */
function getReadContentText(value: TResolvedValue): string {
  if (!isReadContentOutput(value)) return '';
  const contents = (value as TResolvedOutputs).contents;
  if (!Array.isArray(contents)) return '';
  return contents
    .map(item => {
      if (item === null || typeof item !== 'object' || Array.isArray(item)) {
        return '';
      }
      const content = (item as TResolvedOutputs).content;
      return typeof content === 'string' ? content : '';
    })
    .filter(Boolean)
    .join('\n\n');
}

/** Produces a small table model for CSV tables and array-shaped values. */
export function getOutputTablePreview(
  value: TResolvedValue,
): IOutputTablePreview {
  if (isTable(value)) {
    if (value.type === 'table-remote') {
      return { headers: [], rows: [], totalRows: 0 };
    }
    let tableSource = value.data;
    if (isFeather2FormatFromBase64(tableSource)) {
      try {
        tableSource = arrowBufferToCsv(base64ToArrayBuffer(tableSource));
      } catch {
        return { headers: [], rows: [], totalRows: 0 };
      }
    }
    const parsed = Papa.parse<string[]>(tableSource, { skipEmptyLines: true });
    const [headerRow = [], ...bodyRows] = parsed.data;
    return {
      headers: headerRow.map(String),
      rows: bodyRows.map(row => row.map(String)),
      totalRows: bodyRows.length,
    };
  }

  if (!Array.isArray(value)) {
    return { headers: [], rows: [], totalRows: 0 };
  }
  if (value.length === 0) {
    return { headers: ['Value'], rows: [], totalRows: 0 };
  }

  const objectRows = value.filter(
    (item): item is TResolvedOutputs =>
      item !== null && typeof item === 'object' && !Array.isArray(item),
  );
  if (objectRows.length === value.length) {
    const headers = Array.from(
      new Set(objectRows.flatMap(row => Object.keys(row))),
    );
    return {
      headers,
      rows: objectRows.map(row =>
        headers.map(header => toDisplayString(row[header], EM_DASH)),
      ),
      totalRows: objectRows.length,
    };
  }

  return {
    headers: ['Value'],
    rows: value.map(item => [toDisplayString(item, EM_DASH)]),
    totalRows: value.length,
  };
}

/**
 * The name to show for a resolved file: picks the field the shared
 * `getReadableSourceName` reads (inline files carry a name, remote ones only a
 * URL) and leaves the decoding to it.
 */
function getFileName(value: TResolvedFile): string {
  const source = value.type === 'file-inline' ? value.name : value.fileUrl;
  return getReadableSourceName(source);
}

/**
 * True for a file-backed CSV/TSV, which renders as a table rather than a
 * page. Extension only: content type isn't known before download, and a
 * resolved file's name/URL is all a modal-presentation decision ever sees.
 */
export function isCsvFile(value: TResolvedFile): boolean {
  const extension = getFileExtension(getFileName(value));
  return extension === 'csv' || extension === 'tsv';
}

/**
 * True for a file-backed Excel workbook, whose card preview is a flush table.
 * Extension only, same rationale as `isCsvFile`.
 */
export function isExcelFile(value: TResolvedFile): boolean {
  return ['xlsx', 'xls', 'xlsm', 'xlsb'].includes(
    getFileExtension(getFileName(value)),
  );
}

/**
 * True for a file-backed PDF, the only file content the modal renders as a
 * page sheet (`PdfRenderer`'s viewer mode). Extension only, same rationale
 * as `isCsvFile`.
 */
export function isPdfFile(value: TResolvedFile): boolean {
  const extension = getFileExtension(getFileName(value));
  return extension === 'pdf';
}

/**
 * Every extracted field in an IDP value, whichever shape it arrived in — a
 * single extraction, a list of them, or a bundle keyed by document.
 */
function getIDPFields(value: TResolvedValue) {
  if (isIDPOutput(value)) return getIDPFieldsFromOutput(value);
  if (Array.isArray(value)) return value.flatMap(getIDPItemFields);
  return getIDPItemFields(value);
}

/** One-line value used by sm cards and identity summaries. */
export function getOutputCardSummary(
  value: TResolvedValue,
  variant: EOutputCardVariant,
): string {
  if (isRemoteValue(value)) {
    return i18n.t('outputs.card.loading', { ns: 'outputs' });
  }
  switch (variant) {
    case EOutputCardVariant.EMPTY:
      return EM_DASH;
    case EOutputCardVariant.NUMBER:
      return formatNumber(value as number, OUTPUT_CARD_MAX_FRACTION_DIGITS);
    case EOutputCardVariant.TEXT:
      return String(value);
    case EOutputCardVariant.LIST:
      return i18n.t('outputs.card.files', {
        ns: 'outputs',
        count: (value as TResolvedFile[]).length,
      });
    case EOutputCardVariant.TABLE: {
      const preview = getOutputTablePreview(value);
      const columns = preview.headers.length;
      return columns > 0
        ? i18n.t('outputs.card.rowsColumns', {
            ns: 'outputs',
            rows: preview.totalRows,
            columns,
          })
        : i18n.t('outputs.card.table', { ns: 'outputs' });
    }
    case EOutputCardVariant.IDP: {
      const fields = getIDPFields(value);
      const confidences = fields
        .map(field => ('confidence' in field ? field.confidence : undefined))
        .filter(
          (confidence): confidence is number => typeof confidence === 'number',
        );
      const average =
        confidences.length > 0
          ? Math.round(
              confidences.reduce((sum, confidence) => sum + confidence, 0) /
                confidences.length,
            )
          : null;
      return average === null
        ? i18n.t('outputs.card.fields', {
            ns: 'outputs',
            count: fields.length,
          })
        : i18n.t('outputs.card.fieldsConfidence', {
            ns: 'outputs',
            count: fields.length,
            confidence: average,
          });
    }
    case EOutputCardVariant.FILE:
      return getFileName(value as TResolvedFile);
    case EOutputCardVariant.CODE:
      return isHtmlString(value)
        ? i18n.t('outputs.card.htmlDocument', { ns: 'outputs' })
        : i18n.t('outputs.card.markdownDocument', { ns: 'outputs' });
    case EOutputCardVariant.BROWSER:
      return i18n.t('outputs.card.replayReady', { ns: 'outputs' });
    case EOutputCardVariant.DICT:
      return i18n.t('outputs.card.keys', {
        ns: 'outputs',
        count: Object.keys(value as TResolvedOutputs).length,
      });
  }
}

/** Whether the sm summary benefits from the shared md preview hover card. */
export function isOutputCardClipped(
  value: TResolvedValue,
  variant: EOutputCardVariant,
): boolean {
  if (isRemoteValue(value)) return false;
  if (variant === EOutputCardVariant.TEXT) return String(value).length > 36;
  if (variant === EOutputCardVariant.IDP) {
    return isIDPList(value) || isIDPBundle(value) || isIDPBundleList(value);
  }
  return [
    EOutputCardVariant.LIST,
    EOutputCardVariant.TABLE,
    EOutputCardVariant.FILE,
    EOutputCardVariant.CODE,
    EOutputCardVariant.BROWSER,
    EOutputCardVariant.DICT,
  ].includes(variant);
}

/** Serializes each variant according to the packet's copy semantics. */
export function serializeOutputForCopy(
  value: TResolvedValue,
  variant: EOutputCardVariant,
): string {
  switch (variant) {
    case EOutputCardVariant.EMPTY:
      return '';
    case EOutputCardVariant.TEXT:
    case EOutputCardVariant.NUMBER:
      return String(value ?? '');
    case EOutputCardVariant.LIST:
      return (value as TResolvedFile[]).map(getFileName).join('\n');
    case EOutputCardVariant.TABLE: {
      const preview = getOutputTablePreview(value);
      return Papa.unparse([preview.headers, ...preview.rows], {
        delimiter: '\t',
        newline: '\n',
      });
    }
    case EOutputCardVariant.CODE:
      return typeof value === 'string' ? value : getReadContentText(value);
    case EOutputCardVariant.FILE:
      return getFileName(value as TResolvedFile);
    case EOutputCardVariant.BROWSER:
      return JSON.stringify(value, null, 2);
    case EOutputCardVariant.IDP:
    case EOutputCardVariant.DICT:
      return stringify(value, { indent: 2 });
  }
}

/** Escapes cell text so a pasted value is never re-read as markup. */
function escapeClipboardHTML(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/** Rich HTML table payload used alongside TSV for spreadsheet-aware paste. */
export function serializeTableForClipboardHTML(value: TResolvedValue): string {
  const preview = getOutputTablePreview(value);
  const header = preview.headers
    .map(cell => `<th>${escapeClipboardHTML(cell)}</th>`)
    .join('');
  const rows = preview.rows
    .map(
      row =>
        `<tr>${row
          .map(cell => `<td>${escapeClipboardHTML(cell)}</td>`)
          .join('')}</tr>`,
    )
    .join('');
  return `<table><thead><tr>${header}</tr></thead><tbody>${rows}</tbody></table>`;
}

/** Rich clipboard representation for outputs whose structure is meaningful. */
export function serializeOutputForClipboardHTML(
  value: TResolvedValue,
  variant: EOutputCardVariant,
): string | undefined {
  if (variant === EOutputCardVariant.TABLE) {
    return serializeTableForClipboardHTML(value);
  }
  if (
    variant === EOutputCardVariant.CODE ||
    variant === EOutputCardVariant.DICT
  ) {
    return `<pre>${escapeClipboardHTML(serializeOutputForCopy(value, variant))}</pre>`;
  }
  return undefined;
}

/** Text or source body used by the lightweight preview renderers. */
export function getOutputSourceText(value: TResolvedValue): string {
  if (typeof value === 'string') return value;
  return getReadContentText(value);
}

/**
 * Raw text behind the currently displayed value, for the preview modal's
 * Preview/Source toggle — `null` means there's nothing to toggle to (either
 * the variant has no raw-text reading, or it does but isn't resolved in
 * memory yet, as with a `table-remote` value or a CSV/TSV `FILE`, both of
 * which only have bytes after a download completes).
 */
export function getOutputSourceViewText(value: TResolvedValue): string | null {
  if (isHtmlString(value) || isMarkdownString(value)) return value;
  if (
    isTable(value) &&
    value.type === 'table-inline' &&
    !isFeather2FormatFromBase64(value.data)
  ) {
    return value.data;
  }
  return null;
}

/** File names for the shared file-list preview. */
export function getOutputFileNames(value: TResolvedValue): string[] {
  return isOutputFileList(value) ? value.map(getFileName) : [];
}
